import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { PAGE_CHROME_OFF_WHITE } from '../../constants/pageChrome';
import { APP_VERSION } from '../../constants/appVersion';
import { usePageStatusBar } from '../../hooks/usePageStatusBar';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  ACCOUNT_FAQ,
  accountInviteShareMessage,
  accountInviteUrl,
  ACCOUNT_SETTINGS_TITLES,
  accountSupportMailto,
} from './accountSettingsContent';

const listCard =
  'rounded-xl border border-theme-border/80 dark:border-white/10 bg-white dark:bg-white/[0.04] overflow-hidden';

const primaryBtn =
  'w-full rounded-xl bg-theme-text dark:bg-theme-off-white text-white dark:text-theme-text text-[15px] font-semibold py-3.5 px-4 text-center hover:opacity-90 transition-opacity disabled:opacity-40';

const prefsKey = (id: string) => `bravos.account.prefs.${id}`;

function SettingsShell({
  title,
  children,
  stickyFooter,
  hideBottomNavPadding = false,
}: {
  title: string;
  children: React.ReactNode;
  stickyFooter?: React.ReactNode;
  hideBottomNavPadding?: boolean;
}) {
  const navigate = useNavigate();
  return (
    <div
      className={`min-h-screen flex flex-col bg-theme-off-white dark:bg-theme-bg-dark ${
        hideBottomNavPadding ? '' : 'pb-bottom-nav'
      }`}
    >
      <header className="flex-shrink-0 px-3 h-14 flex items-center gap-1 border-b border-theme-border/60 dark:border-white/10">
        <button
          type="button"
          onClick={() => navigate('/account')}
          className="p-2 rounded-lg text-theme-text dark:text-theme-off-white hover:bg-theme-muted/50 dark:hover:bg-white/10"
          aria-label="Back to account"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="text-base font-semibold text-theme-text dark:text-theme-off-white truncate pr-2">
          {title}
        </h1>
      </header>
      <div
        className={`flex-1 px-4 pt-5 max-w-lg mx-auto w-full space-y-4 ${
          stickyFooter ? 'pb-28' : 'pb-10'
        }`}
      >
        {children}
      </div>
      {stickyFooter ? (
        <div className="fixed left-0 right-0 bottom-0 z-[70] border-t border-theme-border/60 dark:border-white/10 bg-theme-off-white dark:bg-theme-bg-dark">
          <div
            className="max-w-lg mx-auto w-full px-4 pt-3"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          >
            {stickyFooter}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function HelpFeedbackBody() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <>
      <p className="text-sm text-theme-text/70 dark:text-theme-off-white/65 leading-relaxed">
        Answers to common questions. Still stuck? Email us and we’ll help.
      </p>
      <div className={listCard}>
        {ACCOUNT_FAQ.map((item, index) => {
          const open = openIndex === index;
          return (
            <div
              key={item.question}
              className="border-b border-theme-border/50 dark:border-white/[0.06] last:border-b-0"
            >
              <button
                type="button"
                className="w-full text-left px-4 py-3.5 text-sm font-medium text-theme-text dark:text-theme-off-white"
                aria-expanded={open}
                onClick={() => setOpenIndex(open ? null : index)}
              >
                {item.question}
              </button>
              {open && (
                <p className="px-4 pb-3.5 text-sm text-theme-text/70 dark:text-theme-off-white/65 leading-relaxed whitespace-pre-line">
                  {item.answer}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function HelpFeedbackEmailSupportButton() {
  const { profile, user } = useAuth();
  const memberId = profile?.id || user?.id || null;
  return (
    <a href={accountSupportMailto(memberId, APP_VERSION)} className={primaryBtn}>
      Contact us
    </a>
  );
}

function InviteFriendsBody() {
  const [copied, setCopied] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadCode = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data, error } = await supabase.rpc('ensure_my_invite_code');
      if (error) throw error;
      const code = typeof data === 'string' ? data.trim() : '';
      if (!code) throw new Error('empty invite code');
      setInviteCode(code);
    } catch {
      setInviteCode(null);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCode();
  }, [loadCode]);

  const share = useCallback(async () => {
    if (!inviteCode) return;
    const message = accountInviteShareMessage(inviteCode);
    try {
      if (navigator.share) {
        // Single `text` payload keeps caption-before-URL order (separate `url` often prepends).
        await navigator.share({
          title: 'Bravos',
          text: message,
        });
        return;
      }
    } catch {
      /* user cancelled or share failed — fall through to clipboard */
    }
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this invite:', message);
    }
  }, [inviteCode]);

  const copyLink = useCallback(async () => {
    if (!inviteCode) return;
    const url = accountInviteUrl(inviteCode);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this link:', url);
    }
  }, [inviteCode]);

  return (
    <>
      <p className="text-sm text-theme-text/70 dark:text-theme-off-white/65 leading-relaxed">
        Share Bravos with someone who’d love training with a coach or discovering workouts on their
        own.
      </p>
      {loadError ? (
        <button type="button" className={primaryBtn} onClick={() => void loadCode()}>
          Couldn’t load invite link — retry
        </button>
      ) : (
        <>
          <button
            type="button"
            className={primaryBtn}
            disabled={loading || !inviteCode}
            onClick={() => void share()}
          >
            {loading ? 'Preparing invite…' : copied ? 'Copied invite' : 'Share invite link'}
          </button>
          <button
            type="button"
            className="w-full rounded-xl border border-theme-border/80 dark:border-white/10 bg-white dark:bg-white/[0.04] text-theme-text dark:text-theme-off-white text-[15px] font-semibold py-3.5 px-4 text-center hover:bg-black/[0.025] dark:hover:bg-white/[0.04] transition-colors disabled:opacity-40"
            disabled={loading || !inviteCode}
            onClick={() => void copyLink()}
          >
            Copy link
          </button>
        </>
      )}
    </>
  );
}

function CommunicationPreferencesBody() {
  const { profile, user } = useAuth();
  const [pushNotifications, setPushNotifications] = useState(false);
  const [marketingNotifications, setMarketingNotifications] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const userId = user?.id || profile?.id;
      if (!userId) {
        const read = (id: string, fallback: boolean) => {
          const raw = localStorage.getItem(prefsKey(id));
          if (raw === null) return fallback;
          return raw === '1';
        };
        if (!cancelled) {
          setPushNotifications(read('pushNotifications', false));
          setMarketingNotifications(read('marketingNotifications', false));
          setHydrated(true);
        }
        return;
      }
      const { data } = await supabase
        .from('users')
        .select('push_notifications_enabled, marketing_notifications_enabled')
        .eq('id', userId)
        .maybeSingle();
      if (cancelled) return;
      const push = data?.push_notifications_enabled ?? false;
      const marketing = data?.marketing_notifications_enabled ?? false;
      setPushNotifications(push);
      setMarketingNotifications(marketing);
      localStorage.setItem(prefsKey('pushNotifications'), push ? '1' : '0');
      localStorage.setItem(prefsKey('marketingNotifications'), marketing ? '1' : '0');
      setHydrated(true);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [user?.id, profile?.id]);

  const persist = async (
    id: 'pushNotifications' | 'marketingNotifications',
    value: boolean,
    setter: (v: boolean) => void
  ) => {
    setter(value);
    localStorage.setItem(prefsKey(id), value ? '1' : '0');
    const userId = user?.id || profile?.id;
    if (!userId) return;
    const patch =
      id === 'pushNotifications'
        ? { push_notifications_enabled: value }
        : { marketing_notifications_enabled: value };
    await supabase.from('users').update(patch).eq('id', userId);
  };

  const rows: {
    id: 'pushNotifications' | 'marketingNotifications';
    label: string;
    description: string;
    value: boolean;
    set: (v: boolean) => void;
  }[] = [
    {
      id: 'pushNotifications',
      label: 'Push notifications',
      description:
        'Alerts for workouts and trainer updates, also for tips or offers when you’ve opted in below.',
      value: pushNotifications,
      set: (v) => {
        void persist('pushNotifications', v, setPushNotifications);
      },
    },
    {
      id: 'marketingNotifications',
      label: 'Tips, offers & product updates',
      description:
        'Optional tips, offers, and Bravos news. Account and security emails are always sent when needed.',
      value: marketingNotifications,
      set: (v) => {
        void persist('marketingNotifications', v, setMarketingNotifications);
      },
    },
  ];

  return (
    <>
      <div className={listCard}>
        {rows.map((row, index) => (
          <label
            key={row.id}
            className={`flex items-start justify-between gap-3 px-4 py-3.5 ${
              index < rows.length - 1 ? 'border-b border-theme-border/50 dark:border-white/[0.06]' : ''
            }`}
          >
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-theme-text dark:text-theme-off-white">
                {row.label}
              </span>
              <span className="mt-1 block text-xs leading-relaxed text-theme-text/60 dark:text-theme-off-white/55">
                {row.description}
              </span>
            </span>
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 accent-theme-text"
              checked={row.value}
              disabled={!hydrated}
              onChange={(e) => row.set(e.target.checked)}
            />
          </label>
        ))}
      </div>
      <p className="text-xs text-theme-text/50 dark:text-theme-off-white/45 leading-relaxed px-1">
        Browser notification permission is controlled by your browser or device settings.
      </p>
    </>
  );
}

export const AccountSettingsPage: React.FC = () => {
  usePageStatusBar({ background: PAGE_CHROME_OFF_WHITE });
  const { settingsKey } = useParams<{ settingsKey: string }>();
  const title = settingsKey ? ACCOUNT_SETTINGS_TITLES[settingsKey] : undefined;

  if (!settingsKey || !title) {
    return <Navigate to="/account" replace />;
  }

  // Legacy routes from the split Help / Feedback pages.
  if (settingsKey === 'help-center' || settingsKey === 'feedback') {
    return <Navigate to="/account/help-feedback" replace />;
  }
  if (settingsKey === 'security-privacy' || settingsKey === 'about') {
    return <Navigate to="/account" replace />;
  }

  if (settingsKey === 'help-feedback') {
    return (
      <SettingsShell
        title={title}
        hideBottomNavPadding
        stickyFooter={<HelpFeedbackEmailSupportButton />}
      >
        <HelpFeedbackBody />
      </SettingsShell>
    );
  }

  let body: React.ReactNode;
  switch (settingsKey) {
    case 'invite-friends':
      body = <InviteFriendsBody />;
      break;
    case 'communication-preferences':
      body = <CommunicationPreferencesBody />;
      break;
    default:
      return <Navigate to="/account" replace />;
  }

  return <SettingsShell title={title}>{body}</SettingsShell>;
};

/** @deprecated Use AccountSettingsPage */
export const AccountSettingsPlaceholder = AccountSettingsPage;
